import React, { useState } from "react";
import { Edit2, Trash2 } from "lucide-react";
import NumberFieldAddon from "../NumberFieldAddon";

const LIBRO_FORM_VACIO = { id: "", titulo: "", autor: "", isbn: "", anioPublicacion: "", fechaLanzamiento: "", stockTotal: 0 };

const SUB_TABS = [
  { id: "libros", label: "Libros" },
  { id: "stock", label: "Stock" }
];

function StockRow({ resumen, libro, onActualizarStockTotal }) {
  const [valor, setValor] = useState(String(resumen.stockTotal));
  const cambiado = Number(valor) !== resumen.stockTotal;
  const quedanNegativo = resumen.quedan < 0;

  return (
    <tr className="hover:bg-gray-50">
      <td className="p-4 text-gray-800 font-medium">{resumen.titulo}</td>
      <td className="p-4">
        <div className="flex items-center gap-2 max-w-[180px]">
          <NumberFieldAddon
            value={valor}
            min="0"
            onChange={(e) => setValor(e.target.value)}
            addon="ejemplares"
            addonPosition="right"
            className="w-full"
          />
          {cambiado && (
            <button
              onClick={() => onActualizarStockTotal(libro, valor)}
              className="px-3 py-2 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg whitespace-nowrap"
            >
              Guardar
            </button>
          )}
        </div>
      </td>
      <td className="p-4 text-center text-gray-700">{resumen.enLibrerias}</td>
      <td className="p-4 text-center text-gray-700">{resumen.vendido}</td>
      <td className="p-4 text-center font-semibold">
        <span className={`px-3 py-1 rounded-full ${quedanNegativo ? "bg-red-50 text-red-700" : "bg-indigo-50 text-indigo-600"}`}>
          {resumen.quedan}
        </span>
      </td>
    </tr>
  );
}

export default function CatalogoTab({
  libroForm,
  setLibroForm,
  libros,
  libroStockSummaries,
  onGuardarLibro,
  onEditarLibro,
  onEliminarLibro,
  onActualizarStockTotal
}) {
  const [subTab, setSubTab] = useState("libros");
  const librosPorId = Object.fromEntries(libros.map((l) => [String(l.id), l]));

  return (
    <div className="space-y-6 max-w-4xl">
      <h2 className="text-2xl font-bold text-gray-800">Catálogo de Libros</h2>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="flex gap-2 flex-wrap">
          {SUB_TABS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setSubTab(opt.id)}
              className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                subTab === opt.id ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {subTab === "libros" && (
        <>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-medium mb-4 text-indigo-900 border-b pb-2">{libroForm.id ? "Editar Libro" : "Añadir Nuevo Libro"}</h3>
            <form onSubmit={onGuardarLibro} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" placeholder="Título del libro" required value={libroForm.titulo} onChange={(e) => setLibroForm({ ...libroForm, titulo: e.target.value })} className="p-3 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none w-full" />
              <input type="text" placeholder="Autor" required value={libroForm.autor} onChange={(e) => setLibroForm({ ...libroForm, autor: e.target.value })} className="p-3 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none w-full" />
              <input type="text" placeholder="ISBN" required value={libroForm.isbn} onChange={(e) => setLibroForm({ ...libroForm, isbn: e.target.value })} className="p-3 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none w-full" />
              <div className="flex flex-col space-y-1">
                <label className="text-sm font-medium text-gray-600">Año de publicación</label>
                <input
                  type="number"
                  required
                  min="1000"
                  max="9999"
                  placeholder="Ej: 2024"
                  value={libroForm.anioPublicacion}
                  onChange={(e) => setLibroForm({ ...libroForm, anioPublicacion: e.target.value })}
                  className="p-3 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none w-full"
                />
              </div>
              <div className="md:col-span-2 flex flex-col space-y-1">
                <label className="text-sm font-medium text-gray-600">Fecha de lanzamiento (opcional)</label>
                <input type="date" value={libroForm.fechaLanzamiento} onChange={(e) => setLibroForm({ ...libroForm, fechaLanzamiento: e.target.value })} className="p-3 border rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none w-full md:w-1/2" />
              </div>
              <div className="md:col-span-2 flex justify-end space-x-3 mt-2">
                {libroForm.id && <button type="button" onClick={() => setLibroForm(LIBRO_FORM_VACIO)} className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">Cancelar</button>}
                <button type="submit" className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg shadow-sm transition-colors">{libroForm.id ? "Actualizar Libro" : "Guardar Libro"}</button>
              </div>
            </form>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-4 text-gray-600 font-medium">Título</th>
                    <th className="p-4 text-gray-600 font-medium hidden md:table-cell">Autor</th>
                    <th className="p-4 text-gray-600 font-medium hidden lg:table-cell">ISBN</th>
                    <th className="p-4 text-gray-600 font-medium">Año</th>
                    <th className="p-4 text-gray-600 font-medium hidden lg:table-cell">Lanzamiento</th>
                    <th className="p-4 text-right text-gray-600 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {libros.map((libro) => (
                    <tr key={libro.id} className="hover:bg-gray-50">
                      <td className="p-4 text-gray-800 font-medium">{libro.titulo}</td>
                      <td className="p-4 text-gray-600 hidden md:table-cell">{libro.autor}</td>
                      <td className="p-4 text-gray-600 hidden lg:table-cell">{libro.isbn || "-"}</td>
                      <td className="p-4 text-gray-600">{libro.anioPublicacion || "-"}</td>
                      <td className="p-4 text-gray-600 hidden lg:table-cell">{libro.fechaLanzamiento || "-"}</td>
                      <td className="p-4 text-right space-x-3">
                        <button onClick={() => onEditarLibro(libro)} className="text-blue-500 hover:text-blue-700" title="Editar"><Edit2 size={18} className="inline" /></button>
                        <button onClick={() => onEliminarLibro(libro.id)} className="text-red-500 hover:text-red-700" title="Eliminar"><Trash2 size={18} className="inline" /></button>
                      </td>
                    </tr>
                  ))}
                  {libros.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-gray-500">Aún no has registrado libros.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {subTab === "stock" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gray-50/50">
            <h3 className="text-lg font-semibold text-gray-800">Stock por libro</h3>
            <p className="text-sm text-gray-500 mt-1">
              Stock total: la tirada inicial del libro. En librerías: lo que hoy tienen las librerías sin vender. Quedan: stock total menos lo vendido en total.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-sm border-b">
                  <th className="p-4 font-medium">Libro</th>
                  <th className="p-4 font-medium">Stock Total</th>
                  <th className="p-4 font-medium text-center">En librerías</th>
                  <th className="p-4 font-medium text-center">Vendido</th>
                  <th className="p-4 font-medium text-center">Quedan</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {libroStockSummaries.map((resumen) => (
                  <StockRow
                    key={resumen.id}
                    resumen={resumen}
                    libro={librosPorId[String(resumen.id)]}
                    onActualizarStockTotal={onActualizarStockTotal}
                  />
                ))}
                {libroStockSummaries.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-gray-500">Aún no has registrado libros.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
